import { z } from 'zod';

import {
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
  SUPPORT_STATUSES,
  type SupportAuthorRole,
  type SupportCategory,
  type SupportPriority,
  type SupportStatus,
} from '../enums';
import { zId, zPagination } from './common';

/* ─── Ввод ──────────────────────────────────────────────────────────────── */

/** Текст сообщения. Пустое сообщение допустимо, если приложены файлы. */
export const zMessageBody = z.string().trim().max(5000);

export const createTicketSchema = z.object({
  subject: z.string().trim().min(3, { message: 'validation.tooShort' }).max(140),
  category: z.enum(SUPPORT_CATEGORIES).default('QUESTION'),
  priority: z.enum(SUPPORT_PRIORITIES).default('NORMAL'),
  body: zMessageBody,
  workspaceId: zId.nullish(),
});
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

export const sendMessageSchema = z.object({
  body: zMessageBody,
});
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const ticketFilterSchema = zPagination.extend({
  status: z.enum(SUPPORT_STATUSES).optional(),
  priority: z.enum(SUPPORT_PRIORITIES).optional(),
  category: z.enum(SUPPORT_CATEGORIES).optional(),
  /** Только открытые: удобнее одним флагом, чем перечислением статусов. */
  activeOnly: z.coerce.boolean().default(false),
  search: z.string().trim().max(140).optional(),
});
export type TicketFilter = z.infer<typeof ticketFilterSchema>;

/** Смена статуса руками — доступна админу; пользователь может только закрыть своё. */
export const updateTicketSchema = z.object({
  status: z.enum(SUPPORT_STATUSES).optional(),
  priority: z.enum(SUPPORT_PRIORITIES).optional(),
});
export type UpdateTicketInput = z.infer<typeof updateTicketSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface SupportAttachmentDto {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /** true — картинка, её показываем превью, а не строкой со скрепкой. */
  isImage: boolean;
}

export interface SupportMessageDto {
  id: string;
  authorId: string | null;
  authorRole: SupportAuthorRole;
  authorName: string | null;
  body: string;
  createdAt: string;
  attachments: SupportAttachmentDto[];
}

export interface SupportTicketDto {
  id: string;
  subject: string;
  category: SupportCategory;
  priority: SupportPriority;
  status: SupportStatus;
  createdAt: string;
  lastMessageAt: string;
  closedAt: string | null;
  unread: number;
  messageCount: number;
  /** Первые слова последнего сообщения — для списка. */
  preview: string;
  workspaceId: string | null;
  workspaceName: string | null;
  /** Заполняется только в админском инбоксе. */
  userId?: string;
  userName?: string;
}

export interface SupportTicketDetailDto extends SupportTicketDto {
  messages: SupportMessageDto[];
}

export interface SupportTicketListResponse {
  items: SupportTicketDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  unreadTotal: number;
}
